/**
 * @file StepperModal Component
 * @description Multi-step wizard modal for cloud integration setup.
 * Manages navigation through multiple steps, validates data at each step,
 * and handles form submission. Features a vertical stepper on the left side (30%)
 * with form content on the right (70%) for better space utilization.
 */

import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useState, useMemo, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';

import { IntegrationData } from '../types';
import './styles.css';

/**
 * Configuration for a single step in the wizard
 * @interface StepConfig
 * @property {string} id - Unique identifier for the step
 * @property {string} title - Step title shown in stepper
 * @property {string} [description] - Optional brief description shown below title and passed to component
 * @property {React.ComponentType} component - The React component to render for this step
 * @property {boolean} [allowBack] - Whether "Back" button should be shown
 * @property {Function} [skip] - Optional function to determine if step should be skipped
 * @property {Function} [onNext] - Optional async function to execute before moving to next step (e.g., API calls)
 */
export interface StepConfig {
  id: string;
  title: string;
  description?: string;
  component: React.ComponentType<StepComponentProps>;
  allowBack?: boolean;
  skip?: (data: IntegrationData) => boolean;
  onNext?: (data: IntegrationData) => Promise<IntegrationData>;
}

/**
 * Props passed to each step component
 * @interface StepComponentProps
 * @property {string} [description] - Optional description from configuration that component can display
 * @property {IntegrationData} data - Current accumulated form data from all previous steps
 * @property {boolean} loading - Loading state (true during async operations)
 * @property {Function} onValidationChange - Callback to notify parent of validation state changes
 * @property {Function} onNext - Callback to move to next step with updated data
 * @property {Function} onBack - Callback to move to previous step
 * @property {Function} onClose - Callback to close the modal and reset data
 * @property {boolean} [allowBack] - Whether back navigation is allowed
 */
export interface StepComponentProps {
  description?: string;
  data: IntegrationData;
  loading: boolean;
  onValidationChange: (isValid: boolean) => void;
  onNext: (stepData: IntegrationData) => void;
  onBack: () => void;
  onClose: () => void;
  allowBack?: boolean;
}

/**
 * Props for the StepperModal component
 * @interface StepperModalProps
 * @property {string} title - Modal title
 * @property {StepConfig[]} steps - Array of step configurations
 * @property {Function} onClose - Callback when modal is closed
 * @property {Function} [onComplete] - Optional callback when all steps are completed
 * @property {IntegrationData} [initialData] - Initial form data
 */
interface StepperModalProps {
  title: string;
  steps: StepConfig[];
  onClose: () => void;
  onComplete?: (data: IntegrationData) => void;
  initialData?: IntegrationData;
}

/**
 * StepperModal - Multi-step wizard modal component
 *
 * Manages a multi-step form flow with:
 * - Vertical progress indicator on left side (30% width) showing completed/active/upcoming steps
 * - Form content area on right side (70% width) for better space utilization
 * - Data accumulation across steps
 * - Validation at each step
 * - Async operations support (API calls)
 * - Back/Next navigation
 * - Skip logic for conditional steps
 * - Responsive design: switches to horizontal stepper on smaller screens
 *
 * Wizard Flow:
 * 1. User fills out current step
 * 2. Clicks "Next" - triggers validation
 * 3. If valid, executes optional onNext handler (e.g., API call)
 * 4. Merges new data with existing form data
 * 5. Moves to next step or completes if last step
 *
 * @component
 * @param {StepperModalProps} props - Component props
 */
const StepperModal: React.FC<StepperModalProps> = ({
  title,
  steps,
  onClose,
  onComplete,
  initialData = {},
}) => {
  // Current step index (0-based)
  const [currentStep, setCurrentStep] = useState(0);
  // Accumulated form data from all steps
  const [formData, setFormData] = useState<IntegrationData>(initialData);
  // Loading state for async operations
  const [loading, setLoading] = useState(false);

  /**
   * Filter out skipped steps based on current form data
   * Some steps may be conditional based on previous selections
   * Recalculates when steps config or form data changes
   */
  const activeSteps = useMemo(() => {
    return steps.filter((step) => {
      if (!step.skip) return true;
      return !step.skip(formData);
    });
  }, [steps, formData]);

  /**
   * Callback for components to notify validation state changes
   * Components call this whenever their validation status changes
   * Note: Currently components handle their own button states,
   * but this callback is available for future enhancements
   * (e.g., showing validation warnings, enabling skip buttons, etc.)
   */
  const handleValidationChange = useCallback((_isValid: boolean) => {
    // Reserved for future use
  }, []);

  /**
   * Handles moving to the next step
   * Flow:
   * 1. Merge new step data with existing form data
   * 2. Execute onNext handler if configured (e.g., API call)
   * 3. If last step: call onComplete and close modal
   * 4. If not last step: update data and increment step
   *
   * @param {IntegrationData} stepData - Data from current step
   */
  const nextStep = useCallback(
    async (stepData: IntegrationData) => {
      const stepConfig = activeSteps[currentStep];

      // Merge new data with existing form data
      const updatedData: IntegrationData = {
        ...formData,
        ...stepData,
      };

      setLoading(true);

      try {
        let result = updatedData;

        // Execute async operation if configured (e.g., API call to verify credentials)
        if (stepConfig.onNext) {
          result = await stepConfig.onNext(updatedData);
        }

        // Check if this is the last step
        if (currentStep === activeSteps.length - 1) {
          showSuccessToast('Integration created successfully!');
          if (onComplete) {
            onComplete(result);
          }
          onClose(); // Close modal after successful completion
        } else {
          // Move to next step
          setLoading(false);
          setFormData(result);
          setCurrentStep(currentStep + 1);
        }
      } catch (error: any) {
        // Handle errors from async operations (e.g., API failures)
        showFailureToast(
          error?.message || 'An error occurred. Please try again.',
        );
        setLoading(false);
      }
    },
    [activeSteps, currentStep, formData, onClose, onComplete],
  );

  /**
   * Handles moving to the previous step
   * Simply decrements the step counter - data is preserved
   */
  const prevStep = useCallback(() => {
    setCurrentStep((prev) => prev - 1);
  }, []);

  /**
   * Renders the vertical progress indicator on the left side of the modal
   * Shows all steps with their status: completed (✓), active (highlighted), or inactive
   *
   * @param {StepConfig[]} steps - Array of step configurations to display
   * @returns {React.ReactElement} Vertical stepper component
   */
  const renderStepper = useCallback(
    (steps: StepConfig[]) => {
      return (
        <div className="stepper-wrapper-vertical">
          {steps.map((step, index) => {
            // Determine step status based on current position
            let status = 'inactive';

            if (index < currentStep) status = 'completed';
            if (index === currentStep) status = 'active';

            // Determine if the connector line after this step should be completed
            const isConnectorCompleted = index < currentStep;

            return (
              <React.Fragment key={step.id}>
                <div className="step-item-vertical">
                  {/* Step circle with number or checkmark */}
                  <div className={`step-circle ${status}`}>
                    {status === 'completed' ? '✓' : index + 1}
                  </div>

                  {/* Step label */}
                  <div className="step-label-vertical">
                    <div className={`step-title ${status}`}>{step.title}</div>
                  </div>
                </div>

                {/* Render vertical connector line between steps (except after last step) */}
                {index < steps.length - 1 && (
                  <div
                    className={`step-connector-vertical ${isConnectorCompleted ? 'completed' : 'inactive'}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      );
    },
    [currentStep],
  );

  // Guard: Prevent rendering if we've somehow gone beyond available steps
  if (currentStep >= activeSteps.length) {
    return null;
  }

  // Get current step configuration and component to render
  const stepConfig = activeSteps[currentStep];
  const StepComponent = stepConfig.component;

  return (
    <Modal
      isOpen
      toggle={onClose}
      size="lg"
      scrollable
      backdrop="static"
      keyboard={false}
    >
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody>
        <div className="modal-body-content">
          {/* Left sidebar - Stepper (30%) */}
          <div className="stepper-sidebar">{renderStepper(activeSteps)}</div>

          {/* Right content area (70%) */}
          <div className="step-content">
            <StepComponent
              description={stepConfig.description}
              data={formData}
              loading={loading}
              onValidationChange={handleValidationChange}
              onNext={nextStep}
              onBack={prevStep}
              onClose={onClose}
              allowBack={stepConfig.allowBack}
            />
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default StepperModal;
